'use client';
import { Box, Heading, Input, Text, Stack, CloseButton, Flex } from '@chakra-ui/react';
import React, { useState } from 'react';

import type { CategoryItem } from '@/constants/product/category';
import { moisturizerItems, cleanserItems, maskItems, sunCareItems } from '@/constants/product/category';
import { brandItems } from '@/constants/product/brand';
import CategorySection from './sections/CategorySection';
import BrandSection from './sections/BrandSection';
import LucideIcon from '@/components/ui/LucideIcon';
import { useSafeRouter } from '@/hooks/route/useSafeRouter';

const getActiveItems = (activeCategory: string): CategoryItem[] => {
  switch (activeCategory) {
    case 'Moisturizer':
      return moisturizerItems;
    case 'Cleanser':
      return cleanserItems;
    case 'Masks':
      return maskItems;
    case 'Sun Care':
      return sunCareItems;
    case 'Brands':
      return brandItems;
    default:
      return moisturizerItems;
  }
};

const categories = ['Moisturizer', 'Cleanser', 'Masks', 'Sun Care', 'Brands'];

interface CategoriesModalProps {
  onClose: () => void;
}

export const CATEGORIES_MODAL_ID = 'CATEGORIES_MODAL';

const ProductCategoryModal = ({ onClose }: CategoriesModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Moisturizer');
  const router = useSafeRouter();

  return (
    <Flex backgroundColor="white" flexDirection="column" height="100dvh" width="100dvw" px="10px">
      <Flex
        height="50px"
        pt="20px"
        pb="10px"
        borderBottomWidth="1px"
        alignItems="center"
        justifyContent="center"
        borderColor="neutral.10"
      >
        <Heading as="h2" textStyle="heading3" fontWeight="medium" color="neutral.90">
          CATEGORIES
        </Heading>

        <CloseButton
          position="absolute"
          right="16px"
          top="14px"
          size="md"
          color="neutral.50"
          aria-label="Close"
          onClick={onClose}
        />
      </Flex>

      <Box py="10px">
        <Box
          position="relative"
          onClick={() => {
            router.push('/search');
            onClose();
          }}
        >
          <Input
            placeholder="Find your products"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            borderRadius={0}
            readOnly
            border="none"
            borderBottom="2px solid"
            borderBottomColor="neutral.10/50"
            pr="40px"
          />
          <Box position="absolute" right="12px" top="50%" transform="translateY(-50%)" pointerEvents="none">
            <LucideIcon name="Search" boxSize="20px" color="brand.40" strokeWidth={1} />
          </Box>
        </Box>
      </Box>

      <Flex height="calc(100% - 140px)" overflow="hidden">
        <Stack width="30%" alignItems="flex-start" gap={0} bg="neutral.5" height="100%" overflowY="auto">
          {categories.map((category) => (
            <Box
              key={category}
              py={3}
              px={4}
              width="100%"
              cursor="pointer"
              bg={activeCategory === category ? 'white' : 'transparent'}
              onClick={() => setActiveCategory(category)}
            >
              <Text textStyle="body3" color="neutral.80" fontWeight="medium">
                {category}
              </Text>
            </Box>
          ))}
        </Stack>

        <Box width="70%" height="100%" overflowY="auto" px="10px" pb="15px">
          {(() => {
            switch (activeCategory) {
              case 'Brands':
                return <BrandSection onClose={onClose} items={getActiveItems(activeCategory)} />;
              default:
                return <CategorySection onClose={onClose} items={getActiveItems(activeCategory)} />;
            }
          })()}
        </Box>
      </Flex>
    </Flex>
  );
};

export default ProductCategoryModal;
